import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'agm_access_token';
const REFRESH_KEY = 'agm_refresh_token';

function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getStoredRefresh() {
  return localStorage.getItem(REFRESH_KEY);
}

function storeTokens(access, refresh) {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

function authHeaders() {
  const token = getStoredToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, {
        headers: authHeaders()
      });
      setUser(data);
    } catch {
      const refresh = getStoredRefresh();
      if (refresh) {
        try {
          const { data: refreshData } = await axios.post(`${API}/api/auth/refresh`, {}, {
            headers: { 'Authorization': `Bearer ${refresh}` }
          });
          if (refreshData.access_token) {
            storeTokens(refreshData.access_token, null);
            const { data } = await axios.get(`${API}/api/auth/me`, {
              headers: { 'Authorization': `Bearer ${refreshData.access_token}` }
            });
            setUser(data);
            setLoading(false);
            return;
          }
        } catch {}
      }
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/api/auth/register`, { name, email, password });
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { headers: authHeaders() });
    } catch {}
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export { getStoredToken, authHeaders };
